import type { JwtPayload } from '../types/auth.types';

export const parseJwt = (token: string): JwtPayload => {
  const base64Url: string = token.split('.')[1];
  const base64: string = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload: string = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  return JSON.parse(jsonPayload) as JwtPayload;
};

/**
 * The user (`sub`) a given access token belongs to, or null. Never throws.
 *
 * <p>DV-3573: `sub` is the "is this still the same session" signal — it
 * changes when a DIFFERENT PERSON logs in, and one token is shared by every
 * tab. (The entity claim stopped meaning that once a switch stopped moving the
 * token.)
 */
export const subjectOf = (token: string | null | undefined): string | null =>
  subjectOfClaims(claimsOf(token));

/**
 * The claims of `token`, or null when there is none or it does not parse. Never throws.
 *
 * <p>DV-3421 review round 5, item 9. The per-request header path needs both the
 * user and the scope root of the SAME token; reading each through its own
 * `parseJwt` decoded every request's token twice. Parse once here and derive
 * both from the payload with {@link subjectOfClaims} / {@link scopeRootOfClaims}.
 */
export const claimsOf = (token: string | null | undefined): JwtPayload | null => {
  if (!token) return null;
  try {
    return parseJwt(token);
  } catch {
    return null;
  }
};

/** {@link subjectOf} for claims already parsed. */
export const subjectOfClaims = (claims: JwtPayload | null): string | null => {
  const sub: unknown = claims?.sub;
  return typeof sub === 'string' && sub.length > 0 ? sub : null;
};

/**
 * {@link subjectOf} for the token in storage. The ONE "whose token is this"
 * read: `tokenSubject()` (reconciliation, the switch's stamp) and the
 * persisted-scope read both use it, so they cannot disagree on an edge input.
 *
 * <p>Lives here, in a leaf, so the scope persistence module — on the
 * pre-render path and imported almost everywhere — does not drag in
 * `callBack.service` and the auth/axios graph behind it. For the same reason
 * the storage key is spelled out rather than imported from `keyClock.config`;
 * it has been `accessToken` since before any of this.
 *
 * <p>Reads without repairing: unlike `getParsedAccessToken()` this does not
 * delete a token it cannot parse. Answering "whose token is this" has no
 * business mutating storage.
 */
export const subjectOfStoredToken = (): string | null => subjectOfClaims(storedClaims());

/**
 * The claims of the stored access token, or null when there is none readable.
 *
 * <p>The one place that knows how the token is stored. Reads without
 * repairing: unlike `getParsedAccessToken()` this does not delete a token it
 * cannot parse — answering a question about the token has no business
 * mutating storage. The key is spelled out for the leaf reason above.
 */
export const storedClaims = (): JwtPayload | null => {
  try {
    const raw = localStorage.getItem('accessToken');
    if (!raw) return null;
    return claimsOf((JSON.parse(raw) as { access_token?: string }).access_token);
  } catch {
    return null;
  }
};

/**
 * A claim that may arrive as a bare string or a one-element array, as a
 * trimmed non-empty string or null (the DV-2663 multivalued-mapper shape).
 *
 * <p>THE one implementation for the UI's own reads of `EntityUUID`.
 * `entity-claim`'s `normalizeEntityClaim` is this function under its original
 * name and re-exports it.
 *
 * <p>Deliberately NOT used for the scope root ({@link scopeRootOfClaims}):
 * that has to agree with the backend, which neither trims nor unwraps an array
 * (DV-3421 review round 5, item 10).
 */
export const claimValue = (raw: string | string[] | undefined): string | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * The entity the stored token's scope is ANCHORED to — `rootEntityUUID` when
 * present and non-blank, else `EntityUUID`. Null when neither is readable.
 *
 * <p>DV-3421. Mirrors commons-security's `EntityScopeResolver.callerEntityUuid()`
 * and entity-service's `getScopeRootUUID()`: the same two claims, in the same
 * order, blank treated as absent, and the value taken RAW — neither side trims
 * it, and neither unwraps the DV-2663 array form (`claim.toString()` turns
 * `['uuid']` into `[uuid]`, which fails UUID parsing and refuses the caller).
 * Review round 5, item 10: the earlier version trimmed and took `array[0]`, so
 * for those shapes it derived a root the backend never uses. Now an array is
 * null ("unknown"), and a padded string comes back padded, as the backend
 * holds it — it matches no entity id, so nothing local answers for it and the
 * server decides. If the backend rule changes, change it here in the same
 * commit. See `JwtPayload.rootEntityUUID` for the casing trap.
 */
export const scopeRootOfStoredToken = (): string | null => scopeRootOfClaims(storedClaims());

/**
 * {@link scopeRootOfStoredToken} for a token the caller already holds. The
 * per-request path uses this rather than the stored read, so the scope header
 * is decided on the same token as the `Authorization` it travels with — the
 * stored one may have been replaced since that token was attached.
 */
export const scopeRootOf = (token: string | null | undefined): string | null =>
  scopeRootOfClaims(claimsOf(token));

/** {@link scopeRootOf} for claims already parsed. */
export const scopeRootOfClaims = (claims: JwtPayload | null): string | null => {
  if (!claims) return null;
  // The backend's order: the root claim unless it is absent or blank, and only
  // then EntityUUID. A present-but-unusable root does NOT fall back — the
  // backend does not either.
  const root = claims.rootEntityUUID;
  const chosen = isPresent(root) ? root : claims.EntityUUID;
  return typeof chosen === 'string' && isPresent(chosen) ? chosen : null;
};

/**
 * Java's `claim != null && !claim.toString().isBlank()`. An array counts as
 * present (its `toString()` is `[...]`); whitespace-only strings do not.
 */
const isPresent = (claim: string | string[] | undefined): claim is string | string[] =>
  Array.isArray(claim) ? true : typeof claim === 'string' && claim.trim().length > 0;
